import React, { useState } from 'react'
import { useParams } from 'react-router'
import { useHistory } from 'react-router-dom'

// Constants
import { routes, RouteParams } from '@sketch/modules-common'

// Hooks
import { useToast } from '@sketch/toasts'
import { useMoveAllCollectionShares } from 'modules/collections/operations'

// Components
import {
  Dropdown,
  Text,
  Modal,
  useModalContext,
  ModalInjectedProps,
  Checkbox,
  Button,
  Link,
  SelectDropdownSubMenu,
} from '@sketch/components'
import {
  CollectionsLoadingState,
  CollectionsErrorState,
} from 'modules/shares/components/ShareActionsDropdown/ManageProjectAndCollectionActions'
import {
  Chevron,
  SubMenuTrigger,
} from 'modules/shares/components/ShareActionsDropdown/ManageProjectAndCollectionActions.styles'

// Types
import {
  CollectionPreviewsFragment,
  CollectionFragment,
  ProjectFragment,
  CollectionForSelectFragment,
  WorkspaceMinimalFragment,
  useGetProjectCollectionsForSelectQuery,
  MoveAllCollectionSharesMutation,
} from '@sketch/gql-types'
import { ErrorHandler } from '@sketch/tracing'

interface MoveAllModalProps extends ModalInjectedProps {
  workspaceId: WorkspaceMinimalFragment['identifier']
  destinationCollection: CollectionForSelectFragment
  originCollection: CollectionFragment
}

type CollectionRoute = RouteParams<'WORKSPACE_COLLECTION'>

const MoveAllModal: React.FC<MoveAllModalProps> = ({
  workspaceId,
  originCollection,
  destinationCollection,
  hideModal,
}) => {
  const history = useHistory()
  const { showToast } = useToast()
  const [removeCollection, setRemoveCollection] = useState(false)
  const { collectionId } = useParams<CollectionRoute>()

  const [moveAllCollectionShares, { loading }] = useMoveAllCollectionShares({
    onCompleted: ({
      moveAllCollectionShares,
    }: MoveAllCollectionSharesMutation) => {
      const { projectIdentifier, targetIdentifier } = moveAllCollectionShares

      const linkToTarget = targetIdentifier
        ? routes.WORKSPACE_COLLECTION.create({
            workspaceId,
            projectId: projectIdentifier,
            collectionId: targetIdentifier,
          })
        : routes.WORKSPACE_PROJECT.create({
            workspaceId,
            projectId: projectIdentifier,
          })

      // Only redirect to the target collection if we're deleting the origin collection
      if (removeCollection) {
        // only show toast if we're on the origin collection view
        // (outside that view we have the useCollectionDeleted subscription)
        if (collectionId === originCollection.identifier) {
          showToast(`“${originCollection.name}” collection removed`)
        }

        history.push(linkToTarget)
      }

      hideModal()
      showToast(
        <>
          All documents moved to &quot;
          {targetIdentifier ? (
            <Link to={linkToTarget}>{destinationCollection.name}</Link>
          ) : (
            destinationCollection.name
          )}
          &quot; collection
        </>,
        'positive'
      )
    },
  })

  const handleMoveClick = () => {
    moveAllCollectionShares({
      variables: {
        originIdentifier: originCollection.identifier,
        targetIdentifier: destinationCollection.identifier,
        removeCollection,
      },
    })
  }

  return (
    <Modal
      title={`Move All Documents to "${destinationCollection.name}"?`}
      onCancel={hideModal}
    >
      <Modal.Body>
        <Text textStyle="copy.tertiary.standard.E">
          All documents in the &quot;<strong>{originCollection.name}</strong>
          &quot; collection will be moved into the &quot;
          <strong>{destinationCollection.name}</strong>&quot; collection.
        </Text>

        <Checkbox
          value={removeCollection ? 1 : 0}
          name="receiveCommentsNotifications"
          checked={removeCollection}
          data-testid="remove-origin-collection"
          label={`Remove the "${originCollection.name}" collection`}
          onChange={() => setRemoveCollection(!removeCollection)}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button disabled={loading} onClick={hideModal}>
          Cancel
        </Button>
        <Button
          variant="primary"
          loading={loading}
          disabled={loading}
          onClick={handleMoveClick}
        >
          Move
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

interface MoveAllToProps {
  workspaceId: WorkspaceMinimalFragment['identifier']
  projectId: ProjectFragment['identifier']
  originCollection: CollectionPreviewsFragment
  collections?: CollectionPreviewsFragment[]
}

export const MoveAllToAction: React.FC<MoveAllToProps> = ({
  workspaceId,
  projectId,
  originCollection,
  collections,
}) => {
  const { showModal } = useModalContext()

  const { loading, error, data } = useGetProjectCollectionsForSelectQuery({
    variables: {
      projectIdentifier: projectId,
    },
    onError: () => {
      ErrorHandler.shouldNeverHappen(
        'Failed to fetch collection data when managing collection'
      )
    },
    skip: collections !== undefined || originCollection.previews.length === 0,
    fetchPolicy: 'cache-and-network',
  })

  if (originCollection.previews.length === 0) {
    return null
  }

  if (loading || error) {
    return (
      <SelectDropdownSubMenu
        value="Move All Documents"
        usePortal
        breakpoint="sm"
        renderTrigger={props => (
          <SubMenuTrigger
            text="Move All Documents"
            additional={<Chevron />}
            {...props}
          />
        )}
        headerItem={<Dropdown.Header>Select a Collection</Dropdown.Header>}
        items={['placeholder']}
        renderItem={() => {
          if (loading) {
            return <CollectionsLoadingState />
          } else if (error) {
            return <CollectionsErrorState />
          }
        }}
      />
    )
  }

  let collectionsFiltered: CollectionForSelectFragment[] = []

  if (collections !== undefined) {
    collectionsFiltered = collections.filter(
      ({ identifier }: { identifier: string }) =>
        identifier !== originCollection.identifier
    )
  } else if (data?.project?.collectionsUnpaginated) {
    collectionsFiltered = data?.project?.collectionsUnpaginated
      .filter(
        ({ identifier }: { identifier: string }) =>
          identifier !== originCollection.identifier
      )
      .map(({ identifier, name }: { identifier: string; name: string }) => ({
        __typename: 'Collection',
        identifier,
        name,
      }))
  }

  const handleClickCollection = (
    destinationCollection: CollectionForSelectFragment
  ) => {
    showModal(MoveAllModal, {
      workspaceId,
      destinationCollection,
      originCollection,
    })
  }

  return (
    <SelectDropdownSubMenu
      value="Move"
      usePortal
      breakpoint="sm"
      renderTrigger={props => (
        <SubMenuTrigger
          text="Move All Documents"
          additional={<Chevron />}
          {...props}
        />
      )}
      headerItem={
        collectionsFiltered.length === 0 ? (
          <Dropdown.Item disabled>No collections available</Dropdown.Item>
        ) : (
          <Dropdown.Header>Select a Collection</Dropdown.Header>
        )
      }
      items={collectionsFiltered}
      renderItem={collection => (
        <Dropdown.Item
          onClick={() => handleClickCollection(collection)}
          data-testid="move-collection-option"
        >
          {collection.name}
        </Dropdown.Item>
      )}
    />
  )
}
